import { useEffect, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { isUndefined } from 'lodash'

import { getDefaultValuesByObjectFields } from '@components/DisplayForm/helpers'
import { useCreateObjectDataRecordWithFixedKey } from '@components/DisplayForm/hooks/useCreateObjectDataRecordWithFixedKey'
import { useFetchObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchObjectDataWithFixedKey'
import { useFetchPreFillObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchPreFillObjectDataWithFixedKey'
import { useFormFields } from '@components/DisplayForm/hooks/useFormFields'
import { useUpdateObjectDataRecordWithFixedKey } from '@components/DisplayForm/hooks/useUpdateObjectDataRecordWithFixedKey'
import { VariableValue } from '@components/Variables/types'

import { isNotEmpty } from '@helpers'
import { FIELD_VALUE_TYPE, FORM_TYPE, PreFillType } from '@constants'
import { ActionRow, ObjectDataRecord, Row } from '@types'

import { useSetInitialValuesForFormFields } from './useSetInitialValuesForFormFields'

type UseFormResetLogicParams = {
  methods: UseFormReturn<Row>
  type: FORM_TYPE
  globalId: string
  formCode: string
  preFill?: PreFillType
  formActions: ActionRow[]
  outerVariables: VariableValue[]
  typeId?: FIELD_VALUE_TYPE
}

export const useFormResetLogic = ({
  methods: formMethods,
  type,
  globalId,
  formCode,
  preFill,
  formActions,
  outerVariables,
  typeId,
}: UseFormResetLogicParams) => {
  const { reset, setValue } = formMethods

  const [, { data: fetchedRow }] = useFetchObjectDataWithFixedKey(globalId, typeId)

  const [, { data: preFillFetchedRow }] = useFetchPreFillObjectDataWithFixedKey(globalId)

  const formFields = useFormFields({ formCode })

  const [, { isLoading: isLoadingUpdateRow }] = useUpdateObjectDataRecordWithFixedKey(globalId)

  const [, { isLoading: isLoadingCreateRow }] = useCreateObjectDataRecordWithFixedKey(globalId)
  // TODO Рефакторинг логики с preFill
  const row = useMemo(() => {
    if (type === FORM_TYPE.CREATE) {
      if (preFill) {
        return preFillFetchedRow
      }

      return {}
    }

    return fetchedRow
  }, [type, fetchedRow, preFillFetchedRow, preFill])

  const defaultValues: ObjectDataRecord = useMemo(
    () => (row ? getDefaultValuesByObjectFields(formFields, row) : {}),
    [formFields, row]
  )

  const emptyDefaultValues: ObjectDataRecord = useMemo(
    () => getDefaultValuesByObjectFields(formFields, {}),
    [formFields]
  )

  const { setInitialValuesForFormFields, isSetInitialValues } = useSetInitialValuesForFormFields(
    outerVariables,
    formActions,
    formMethods,
    defaultValues
  )

  const setPreFilledValues = () => {
    // Ресетим форму пустыми значениями, чтобы в дальнейшем корректно отрабатывали dirty состояния предзаполненных полей
    reset(emptyDefaultValues)
    // Предзаполняем поля и делаем их dirty
    Object.entries(defaultValues).forEach(([key, value]) => {
      if (isNotEmpty(value)) {
        setValue(key, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
      }
    })
  }

  useEffect(() => {
    // Форма создания
    if (type === FORM_TYPE.CREATE) {
      // Прововеряем есть ли статический или динамический Pre Fill
      const hasPreFill = !isUndefined(preFill)
      if (hasPreFill) setPreFilledValues()

      if (isSetInitialValues) setInitialValuesForFormFields()

      if (hasPreFill || isSetInitialValues) return

      reset(defaultValues)

      return
    }
    // Форма редактирования
    if (defaultValues) {
      reset({ ...defaultValues })

      return
    }
  }, [defaultValues, type, isSetInitialValues])

  useEffect(() => {
    if ((!isLoadingUpdateRow || !isLoadingCreateRow) && type !== FORM_TYPE.CREATE) {
      // reset({ ...defaultValues, ...row })

      reset({ ...defaultValues })
    }
  }, [
    // row,
    defaultValues,
    globalId,
  ])

  return { defaultValues }
}
